import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import apiClient from '../api/api-client'
import { AxiosResponse } from 'axios'

import { ModulHandbuch, ModulHandbuchCollection } from '../model'
import { toSemesterSlug } from '../composables/useSemester'

export const useModulHandbuchStore = defineStore('modulhandbuch', () => {
    const modulHandbuecher = ref<Array<ModulHandbuch>>([])
    const totalItems = ref<number>(0)
    const loadingArray = ref<Array<string>>([])
    const errorArray = ref({ fetch: false, change: false, create: false })
    const isLoading = computed(() => loadingArray.value.length > 0)
    const hasErrors = computed(
        () => errorArray.value.fetch || errorArray.value.change || errorArray.value.create
    )
    const getErrors = computed(() => errorArray)
    const getModulhandbuecher = computed<ModulHandbuch[]>(() =>
        modulHandbuecher.value
            .slice()
            .sort((a: ModulHandbuch, b: ModulHandbuch) =>
                a.studiengangId.localeCompare(b.studiengangId)
            )
    )
    const getModulHandbuchById = function getModulHandbuchById(
        modulHandbuchId: string
    ): ModulHandbuch | undefined {
        return modulHandbuecher.value.find(
            (modulHandbuch) => modulHandbuch.modulHandbuchId === modulHandbuchId
        )
    }

    /**
     * Findet ein Modulhandbuch anhand der Studiengang-ID und eines Semester-Slugs.
     * Beispiel: getModulhandbuchByStudiengangAndSemesterSlug(id, "sose2026")
     * passt zu einem MHB mit semester "SoSe 2026".
     */
    const getModulhandbuchByStudiengangAndSemesterSlug = function (
        studiengangId: string,
        semesterSlug: string
    ): ModulHandbuch | undefined {
        return modulHandbuecher.value.find(
            (mhb) =>
                mhb.studiengangId === studiengangId &&
                toSemesterSlug(mhb.semester ?? '') === semesterSlug
        )
    }

    const removeModulhandbuchFromState = (modulHandbuchId: ModulHandbuch['modulHandbuchId']): void => {
        const index = modulHandbuecher.value.findIndex(
            (item: ModulHandbuch) => item.modulHandbuchId === modulHandbuchId
        )

        if (index !== -1) {
            modulHandbuecher.value.splice(index, 1)
        }
    }

    // const replaceModulHandbuchInState = (newModulHandbuch: ModulHandbuch) => {
    //     modulHandbuecher.value = [
    //         ...modulHandbuecher.value.filter(
    //             (modulHandbuch) =>
    //                 modulHandbuch.modulHandbuchId !== newModulHandbuch.modulHandbuchId
    //         ),
    //         newModulHandbuch
    //     ]
    // }

    async function fetchModulHandbuecher(): Promise<void> {
        loadingArray.value.push('fetch')
        errorArray.value.fetch = false
        modulHandbuecher.value = []
        const url: string = '/api/modulhandbuecher'
        try {
            const response: AxiosResponse<ModulHandbuchCollection> = await apiClient.get(url, {
                params: {
                    pagination: false
                }
            })
            modulHandbuecher.value = response.data['hydra:member']
            totalItems.value = response.data['hydra:totalItems'] ?? 0
        } catch (error: any) {
            errorArray.value.fetch = error.response
        } finally {
            loadingArray.value.splice(loadingArray.value.indexOf('fetch'), 1)
        }
    }

    async function uploadModulHandBuecherFile(file: string) {
        loadingArray.value.push('uploadModulHandbuecher')
        errorArray.value.change = false
        try {
            await apiClient.post('/api/modulhandbuecher/upload', {
                fileData: file
            })

            return true
        } catch (error: any) {
            errorArray.value.change = error.response
        } finally {
            await fetchModulHandbuecher()
        }
    }

    async function addModulToModulHandbuch(modulhandbuchId: string, modulId: string): Promise<void> {
        const response: AxiosResponse<ModulHandbuch> = await apiClient.post(
            '/api/modulhandbuecher/add_modul',
            {
                modulhandbuch: `/api/modulhandbuecher/${modulhandbuchId}`,
                modul: `/api/module/${modulId}`,
            },
            { headers: { 'Content-Type': 'application/ld+json' } },
        )
        const index = modulHandbuecher.value.findIndex((m) => m.modulHandbuchId === modulhandbuchId)
        if (index !== -1) {
            modulHandbuecher.value[index] = response.data
        }
    }

    async function removeModulhandbuch(modulHandbuch: ModulHandbuch) {
        loadingArray.value.push('removeModulhandbuch')
        errorArray.value.change = false
        try {
            await apiClient.post('/api/modulhandbuecher/remove', {
                modulHandbuch: modulHandbuch['@id']
            })
            removeModulhandbuchFromState(modulHandbuch.modulHandbuchId)
        } catch (error: any) {
            errorArray.value.change = error.response
        } finally {
            loadingArray.value.splice(loadingArray.value.indexOf('removeModulhandbuch'), 1)
        }
    }

    return {
        modulHandbuecher,
        totalItems,
        loadingArray,
        errorArray,
        isLoading,
        hasErrors,
        getErrors,
        getModulhandbuecher,
        getModulHandbuchById,
        getModulhandbuchByStudiengangAndSemesterSlug,
        fetchModulHandbuecher,
        uploadModulHandBuecherFile,
        addModulToModulHandbuch,
        removeModulhandbuch
    }
})
